// /////////////////////////////////////////////////////////////
// [ファイル] ProjectAttachmentsPanel.tsx
// [概要] 案件の添付ファイルタブ（推進案件機能統合 リリース1）
//        旧「営業案件管理リスト」のモーダル（案件詳細）の添付ファイルタブと同じ機能:
//        アップロード枠（ドラッグ＆ドロップ／ファイル選択、PDF・JPEG・PNG）、種類別アイコン付きの一覧、
//        クリックで画像・PDF をプレビュー（それ以外は別タブ）、ダウンロード、削除確認。
//        保存先も旧画面と同じ Firebase Storage の sales-projects/{projectId}/ で、両画面から同じファイルが見える。
// /////////////////////////////////////////////////////////////
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, List, ListItem, ListItemIcon, ListItemText, Paper, Typography,
} from '@mui/material';
import { ref, listAll, uploadBytes, getDownloadURL, getMetadata, deleteObject } from 'firebase/storage';
import { storage } from '../../firebase/firebase';
import * as Icons from '../../constants/icons';
import LoadingView from '../LoadingView';
import { useNotification } from '../../providers/NotificationProvider';
import { useConfirm } from '../../providers/ConfirmProvider';
import { STANDARD_CTA_BUTTON_COMPACT_SX, STANDARD_TEXT_BUTTON_SX } from '../../constants/buttonStyles';

interface FileRow { name: string; url: string; size: number; contentType?: string; updated: string }

// 旧画面と同じ対応形式
const ACCEPT = ['image/jpeg', 'image/png', 'application/pdf'];
const ACCEPT_ATTR = ACCEPT.join(',');

const pad = (n: number) => String(n).padStart(2, '0');
const fmtUpdated = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function ProjectAttachmentsPanel({ projectId }: { projectId: string }) {
  const notify = useNotification();
  // 通知 API は参照が変わるため load の依存に入れない（無限ループ防止）
  const notifyRef = useRef(notify); notifyRef.current = notify;
  const confirm = useConfirm();
  const [files, setFiles] = useState<FileRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState<FileRow | null>(null);
  // Storage からのプレビュー取得にはラグがあるため、img / iframe の読み込み完了までローディングを重ねる
  const [previewLoading, setPreviewLoading] = useState(false);
  // 拡張子が JPG でも中身が HEIC（iPhone の写真）などブラウザで表示できない画像がある。その場合は案内を出す
  const [previewError, setPreviewError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listAll(ref(storage, `sales-projects/${projectId}`));
      const rows = await Promise.all(res.items.map(async (item) => {
        const [url, meta] = await Promise.all([getDownloadURL(item), getMetadata(item)]);
        return { name: item.name, url, size: meta.size, contentType: meta.contentType, updated: meta.updated };
      }));
      // 新しい順
      rows.sort((a, b) => new Date(b.updated).getTime() - new Date(a.updated).getTime());
      setFiles(rows);
    } catch (e) {
      console.error(e);
      notifyRef.current.error('添付ファイルの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const upload = async (picked: File[]) => {
    if (picked.length === 0) return;
    const ok = picked.filter((f) => ACCEPT.includes(f.type));
    const skipped = picked.length - ok.length;
    if (skipped > 0) notify.warning(`対応していない形式のファイル ${skipped} 件は除外しました（対応形式: PDF, JPEG, PNG）`);
    if (ok.length === 0) return;
    setUploading(true);
    try {
      for (const f of ok) await uploadBytes(ref(storage, `sales-projects/${projectId}/${f.name}`), f);
      notify.success(`${ok.length}件のファイルをアップロードしました`);
      load();
    } catch (err) {
      console.error(err);
      notify.error('アップロードに失敗しました');
    } finally {
      setUploading(false);
    }
  };

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = ''; // 同じファイルを続けて選べるようにリセット
    upload(picked);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (uploading) return;
    upload(Array.from(e.dataTransfer.files || []));
  };

  const open = (f: FileRow) => {
    const isImage = f.contentType?.startsWith('image/');
    const isPdf = f.contentType === 'application/pdf';
    if (isImage || isPdf) { setPreviewLoading(true); setPreviewError(false); setPreview(f); }
    else window.open(f.url, '_blank', 'noopener,noreferrer');
  };

  const remove = async (f: FileRow) => {
    const ok = await confirm({
      title: 'ファイルの削除',
      message: <>「{f.name}」を削除してもよろしいですか？<Typography component="span" variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>※この操作は取り消せません</Typography></>,
      confirmText: '削除する', destructive: true,
    });
    if (!ok) return;
    setUploading(true);
    try {
      await deleteObject(ref(storage, `sales-projects/${projectId}/${f.name}`));
      notify.success('ファイルを削除しました');
      load();
    } catch (err) {
      console.error(err);
      notify.error('削除に失敗しました');
    } finally {
      setUploading(false);
    }
  };

  const isPdfPreview = preview?.contentType === 'application/pdf';

  return (
    <Box sx={{ p: 1 }}>
      {/* アップロードエリア（ドラッグ＆ドロップ、または選択） */}
      <Box
        onDragOver={(e) => { e.preventDefault(); if (!dragOver) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        sx={{
          mb: 2, py: 1.5, px: 2, border: '2px dashed', borderRadius: 2, textAlign: 'center',
          borderColor: dragOver ? 'primary.main' : 'divider', bgcolor: dragOver ? 'action.selected' : 'grey.50', transition: 'all .15s',
        }}
      >
        <Icons.CloudUpload sx={{ fontSize: 24, color: dragOver ? 'primary.main' : 'text.secondary', mb: 0.5, opacity: dragOver ? 1 : 0.5 }} />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          ファイルをドラッグ＆ドロップ、または選択してください
        </Typography>
        <input ref={inputRef} accept={ACCEPT_ATTR} style={{ display: 'none' }} multiple type="file" onChange={onPick} />
        <Button size="small" startIcon={<Icons.Add />} sx={STANDARD_CTA_BUTTON_COMPACT_SX} onClick={() => inputRef.current?.click()} disabled={uploading}>
          ファイルを選択
        </Button>
        <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary', fontSize: '0.7rem' }}>
          対応形式: PDF, JPEG, PNG
        </Typography>
      </Box>

      {/* アップロード・削除の進捗 */}
      {uploading && <LoadingView message="処理中..." height="60px" size={28} />}

      {/* ファイル一覧 */}
      {loading ? (
        <LoadingView message="読み込み中..." height="100px" size={30} />
      ) : files.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          添付されているファイルはありません。
        </Typography>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
          <List disablePadding>
            {files.map((f, idx) => {
              const isImage = f.contentType?.startsWith('image/');
              const isPdf = f.contentType === 'application/pdf';
              return (
                <React.Fragment key={f.name}>
                  {idx > 0 && <Divider />}
                  <ListItem
                    onClick={() => open(f)}
                    secondaryAction={
                      <IconButton size="small" color="error" title="削除" disabled={uploading} onClick={(e) => { e.stopPropagation(); remove(f); }}>
                        <Icons.Delete sx={{ fontSize: 18 }} />
                      </IconButton>
                    }
                    sx={{ cursor: 'pointer', py: 1, '&:hover': { bgcolor: 'grey.50', '& .file-name': { color: 'primary.main', textDecoration: 'underline' } } }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {isImage ? <Icons.Image color="primary" sx={{ fontSize: 24 }} />
                        : isPdf ? <Icons.PictureAsPdf color="error" sx={{ fontSize: 24 }} />
                          : <Icons.InsertDriveFile color="action" sx={{ fontSize: 24 }} />}
                    </ListItemIcon>
                    <ListItemText
                      primary={f.name}
                      secondary={`${(f.size / 1024 / 1024).toFixed(2)} MB • ${fmtUpdated(f.updated)}`}
                      slotProps={{
                        primary: { className: 'file-name', variant: 'body2', sx: { fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 'calc(100% - 100px)' } },
                        secondary: { variant: 'caption' },
                      }}
                    />
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        </Paper>
      )}

      {/* 画像・PDF プレビュー */}
      <Dialog open={Boolean(preview)} onClose={() => setPreview(null)} maxWidth="xl" fullWidth disableScrollLock>
        <DialogTitle sx={{ m: 0, py: 0.25, px: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ ml: 1, fontWeight: 'bold', fontSize: '0.8125rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{preview?.name}</Typography>
          <IconButton size="small" onClick={() => setPreview(null)} sx={{ p: 0.5 }}><Icons.Close sx={{ fontSize: 18 }} /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: 'black', display: 'flex', justifyContent: 'center', position: 'relative', height: isPdfPreview ? '80vh' : 'auto', minHeight: 200 }}>
          {previewLoading && (
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,0,0,0.6)', zIndex: 1 }}>
              <LoadingView message="プレビューを読み込み中..." height="120px" size={36} />
            </Box>
          )}
          {preview && (isPdfPreview ? (
            <Box component="iframe" src={`${preview.url}#toolbar=0`} title="PDF Preview" onLoad={() => setPreviewLoading(false)} sx={{ width: '100%', height: '100%', border: 'none' }} />
          ) : (
            previewError ? (
              <Box sx={{ color: 'grey.300', textAlign: 'center', py: 6, px: 2 }}>
                <Icons.Image sx={{ fontSize: 40, opacity: 0.5 }} />
                <Typography variant="body2" sx={{ mt: 1 }}>この画像はブラウザで表示できない形式です（iPhone の HEIC 形式など）。</Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>右下の「ダウンロード」から保存して開いてください。</Typography>
              </Box>
            ) : (
              <Box component="img" src={preview.url} alt={preview.name} onLoad={() => setPreviewLoading(false)} onError={() => { setPreviewLoading(false); setPreviewError(true); }} sx={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }} />
            )
          ))}
        </DialogContent>
        <DialogActions sx={{ py: 0.25, px: 1 }}>
          <Button size="small" component="a" href={preview?.url} download={preview?.name} target="_blank" rel="noopener" startIcon={<Icons.Save />} sx={{ ...STANDARD_TEXT_BUTTON_SX, minHeight: 0, py: 0.25 }}>
            ダウンロード
          </Button>
          <Button size="small" onClick={() => setPreview(null)} sx={{ ...STANDARD_TEXT_BUTTON_SX, minHeight: 0, py: 0.25 }}>閉じる</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
